import useSWR from 'swr';
import { useAuth } from '@/contexts/auth-context';

interface CachedSocialConnectionsReturn {
    isRefreshing: boolean;
    refreshError: Error | null;
    triggerRefresh: () => Promise<any>;
}

/**
 * Cached hook for triggering social connection refresh (e.g., TikTok token refresh)
 * Uses SWR to avoid redundant refresh calls
 */
export function useCachedSocialConnections(): CachedSocialConnectionsReturn {
    const { user } = useAuth();

    const {
        error: refreshError,
        isLoading: isRefreshing,
        mutate: triggerRefresh
    } = useSWR(
        user?.id ? ['social-connections-refresh', user.id] : null,
        async () => {
            try {
                await fetch('/api/user/social-connections');
                return { success: true };
            } catch (error) {
                console.error('Error refreshing social connections:', error);
                throw error;
            }
        },
        {
            revalidateOnMount: true,
            revalidateOnFocus: false,
            dedupingInterval: 60000, // Only refresh once per minute max
            focusThrottleInterval: 60000,
        }
    );

    return {
        isRefreshing,
        refreshError: refreshError ?? null,
        triggerRefresh,
    };
}
